import { apiClient } from './api';
import SessionDB from '../utils/sessionStorage';
import {
    API_ENDPOINTS,
    APIHeaders,
    APIHeadersWithAuth,
} from '../globalConstants';
import {
    getShops,
    setShops,
    setActiveShopId,
    ShopSummary,
    AuthUser,
} from '../utils/shopSession';

export interface LoginResult {
    user: AuthUser & { setupDone: boolean };
    accessToken: string;
    refreshToken: string;
}

export interface SetupStatus {
    setupRequired: boolean;
}

export interface SetupResult {
    user: AuthUser & { setupDone: boolean };
    shop: ShopSummary;
    shopId: number;
    setupDone: boolean;
}

export function apiErrorMessage(err: any): string {
    const msg: string = err?.message ?? '';
    if (msg.includes('401')) return 'Invalid username or password';
    if (msg.includes('403')) return 'You do not have permission to do this';
    if (msg.includes('409')) return 'Setup has already been completed';
    if (msg.includes('Failed to fetch'))
        return 'Could not reach the server. Is the backend running?';
    return 'Something went wrong. Please try again.';
}

export async function login(
    username: string,
    password: string
): Promise<LoginResult> {
    const res = await apiClient.post<{ data: LoginResult }>(
        API_ENDPOINTS.auth.LOGIN,
        { username, password },
        { headers: APIHeaders }
    );
    return res.data;
}

export async function fetchSetupStatus(): Promise<SetupStatus> {
    const res = await apiClient.get<{ data: SetupStatus }>(
        API_ENDPOINTS.setup.STATUS,
        { headers: APIHeadersWithAuth() }
    );
    return res.data;
}

export async function completeSetup(data: {
    name: string;
    address?: string;
    phone?: string;
    email?: string;
    currency?: string;
    dateFormat?: 'BS' | 'AD';
    taxRate?: number;
}): Promise<SetupResult> {
    const res = await apiClient.post<{ data: SetupResult }>(
        API_ENDPOINTS.setup.CREATE,
        data,
        { headers: APIHeadersWithAuth() }
    );
    return res.data;
}

export async function fetchShops(): Promise<ShopSummary[]> {
    const res = await apiClient.get<{ data: ShopSummary[] }>(
        API_ENDPOINTS.shops.LIST,
        { headers: APIHeadersWithAuth() }
    );
    return res.data;
}

export async function logout(allDevices = false): Promise<void> {
    try {
        await apiClient.post(
            API_ENDPOINTS.auth.LOGOUT,
            { refreshToken: SessionDB.getRefreshToken(), all: allDevices },
            { headers: APIHeadersWithAuth() }
        );
    } catch {
        /* ignore network errors on logout; client discards tokens anyway */
    }
}

export async function refreshToken(refreshToken: string): Promise<LoginResult> {
    const res = await apiClient.post<{ data: LoginResult }>(
        API_ENDPOINTS.auth.REFRESH_TOKEN,
        { refreshToken },
        { headers: APIHeaders }
    );
    return res.data;
}

/** Fetch the user's shops and persist them, selecting the first as active. */
export async function loadShopsAndSelectDefault(): Promise<ShopSummary[]> {
    const shops = await fetchShops();
    setShops(shops);
    if (shops.length && getShops().length) {
        setActiveShopId(shops[0].id);
    }
    return shops;
}
